import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ErrorSummary, Field } from '@/components/forge/Field'
import { ForgeButton } from '@/components/forge/ForgeButton'
import { BrandLockup } from '@/components/forge/BrandLockup'
import { Panel } from '@/components/forge/Panel'
import { Mono } from '@/components/forge/primitives'
import { useRequestPasswordReset } from '@/features/auth/mutations'
import { useDocumentTitle } from '@/hooks/useDocumentTitle'
import { ERROR_CODES } from '@/services/apiError'
import { ACCOUNT_ALIAS_PATTERN, EMAIL_PATTERN, readStoredAlias } from './loginFields'

/**
 * Ask for a password reset link.
 *
 * ── Two fields, because a person is only a person within an account ─────
 * The same pair sign-in asks for, and for the same reason: the same address
 * under two accounts is two different people, so the alias cannot be inferred
 * from the address. `/auth/forgot-password` takes both and would find nobody
 * with one.
 *
 * The alias is pre-filled from the same `localStorage` key the sign-in screen
 * writes, so somebody who has signed in on this terminal before does not have to
 * find their organisation's code again at the moment they have already lost
 * something. It is read, never written here — an alias typed on a screen that
 * confirms nothing is not a fact worth remembering.
 *
 * ── The confirmation deliberately confirms nothing ──────────────────────
 * The API answers identically whether the account exists, the address exists
 * under it, or neither does, and it says so in its own message: "if an account
 * with that email exists". That is not a hedge to be tidied up on the way to the
 * screen — it is the whole security property. A page that said "sent" for a real
 * address and "no such account" for an unknown one would confirm which addresses
 * have accounts on this platform, to anybody, without signing in.
 *
 * So the success state is phrased as a conditional, it repeats the address back
 * so a typo is visible, and it does not say a mail is on its way to THAT
 * address. It also offers the request again, because a mistyped alias is the
 * likeliest reason nothing arrives and there is nothing here that can detect it.
 *
 * ── What it does not do ─────────────────────────────────────────────────
 *  - **No resend timer.** The API applies no throttle here that this client
 *    knows of, so a countdown would be this screen inventing a rule the server
 *    does not enforce. A 429 IS handled, for the day one appears — the message
 *    is the only thing here that would otherwise fall through to "something went
 *    wrong" on a perfectly comprehensible answer.
 *  - **No "check your spam folder" copy on the form.** It belongs on the
 *    confirmation, where somebody is actually waiting, and it is there.
 *  - **No link to create an account.** This platform has no self-service
 *    sign-up; accounts are invited by an administrator.
 */
export function ForgotPasswordPage() {
  const { t } = useTranslation(['auth', 'common'])
  useDocumentTitle(t('forgotPassword.meta.title'), t('forgotPassword.meta.description'))

  const request = useRequestPasswordReset()

  const [accountAlias, setAccountAlias] = useState(readStoredAlias)
  const [email, setEmail] = useState('')
  const [errors, setErrors] = useState({})
  const [submitted, setSubmitted] = useState(false)

  const summaryRef = useRef(null)

  function validate() {
    const next = {}
    if (!accountAlias.trim()) next.accountAlias = t('fields.accountAlias.required')
    else if (!ACCOUNT_ALIAS_PATTERN.test(accountAlias.trim())) {
      next.accountAlias = t('fields.accountAlias.format')
    }
    if (!email.trim()) next.email = t('fields.email.required')
    else if (!EMAIL_PATTERN.test(email.trim())) next.email = t('fields.email.format')
    return next
  }

  function handleSubmit(event) {
    event.preventDefault()
    setSubmitted(true)
    request.reset()

    const found = validate()
    setErrors(found)
    if (Object.keys(found).length) return

    request.mutate({ accountAlias, email })
  }

  // Focus the summary when a submit fails, so a keyboard or screen-reader user
  // learns that something happened. The sign-in screen's behaviour.
  useEffect(() => {
    if (submitted && Object.keys(errors).length) summaryRef.current?.focus()
  }, [submitted, errors])

  /**
   * Only transport and shape failures reach here.
   *
   * There is no "unknown account" case to render, because the API does not have
   * one — see the header. `validation` means the address was malformed in a way
   * the client's permissive pattern let through.
   */
  const serverError = request.isError
    ? request.error?.code === ERROR_CODES.VALIDATION
      ? t('forgotPassword.errors.invalidRequest')
      : request.error?.code === ERROR_CODES.RATE_LIMITED
        ? t('forgotPassword.errors.tooMany')
        : request.error?.code === ERROR_CODES.NETWORK || request.error?.code === ERROR_CODES.TIMEOUT
          ? t('errors.unavailable')
          : t('common:errors.unknown')
    : null

  return (
    <div className="flex min-h-screen flex-col bg-page">
      <header className="border-b border-hairline bg-card">
        <div className="mx-auto flex min-h-14 w-full max-w-[1240px] items-center px-14 py-2 max-xl:px-8 max-lg:px-5">
          <Link to="/" className="flex items-center">
            <BrandLockup size="md" />
          </Link>
        </div>
      </header>

      <main className="flex flex-1 items-start justify-center px-5 py-14 max-lg:py-8">
        <div className="w-full max-w-[27rem]">
          {request.isSuccess ? (
            <>
              <h1 className="m-0 text-display text-ink">{t('forgotPassword.sentHeading')}</h1>
              {/* Conditional, and phrased as the API phrases it. The address is
                  repeated so a typo is visible, NOT to imply a mail went to it. */}
              <p className="mt-3 text-body leading-[1.6] text-ink-dim">
                {t('forgotPassword.sentIntro')}
              </p>

              <Panel className="mt-7 p-6 max-lg:p-4.5">
                <dl className="m-0 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1.5">
                  <dt className="text-body-sm text-ink-dim">
                    {t('fields.accountAlias.label')}
                  </dt>
                  <dd className="m-0">
                    <Mono className="text-body-sm text-ink">{accountAlias.trim()}</Mono>
                  </dd>
                  <dt className="text-body-sm text-ink-dim">{t('fields.email.label')}</dt>
                  <dd className="m-0">
                    <Mono className="text-body-sm text-ink">{email.trim()}</Mono>
                  </dd>
                </dl>

                <p className="mt-4 mb-0 text-body-sm text-ink-dim">
                  {t('forgotPassword.sentNote')}
                </p>

                <div className="mt-5 flex flex-wrap items-center gap-3">
                  <ForgeButton asChild variant="primary">
                    <Link to="/login">{t('forgotPassword.backToSignIn')}</Link>
                  </ForgeButton>
                  {/* Offered because a mistyped alias is the likeliest reason
                      nothing arrives, and nothing here can detect one. */}
                  <ForgeButton onClick={() => request.reset()}>
                    {t('forgotPassword.tryAgain')}
                  </ForgeButton>
                </div>
              </Panel>
            </>
          ) : (
            <>
              <h1 className="m-0 text-display text-ink">{t('forgotPassword.heading')}</h1>
              <p className="mt-3 text-body leading-[1.6] text-ink-dim">
                {t('forgotPassword.intro')}
              </p>

              <Panel className="mt-7 p-6 max-lg:p-4.5">
                {/* noValidate: the browser's own bubbles cannot be styled, are
                    not translated by our layer, and pre-empt the summary. */}
                <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-4.5">
                  {submitted && Object.keys(errors).length ? (
                    <ErrorSummary
                      ref={summaryRef}
                      id="forgot-errors"
                      title={t('errors.summaryTitle', { count: Object.keys(errors).length })}
                      errors={errors}
                      className="-mb-1"
                    />
                  ) : null}

                  {serverError ? (
                    <p
                      role="alert"
                      className="m-0 rounded-sm border border-hairline stripe-red bg-card p-3.5 text-body-sm text-on-red"
                    >
                      {serverError}
                    </p>
                  ) : null}

                  <Field
                    id="accountAlias"
                    label={t('fields.accountAlias.label')}
                    hint={t('fields.accountAlias.hint')}
                    error={submitted ? errors.accountAlias : undefined}
                    required
                    value={accountAlias}
                    onChange={(event) => setAccountAlias(event.target.value)}
                    autoComplete="organization"
                    // Neither auto-capitalised nor upper-cased: the API compares
                    // this string exactly.
                    autoCapitalize="none"
                    spellCheck={false}
                    inputClassName="mono"
                  />

                  <Field
                    id="email"
                    type="email"
                    inputMode="email"
                    label={t('fields.email.label')}
                    hint={t('fields.email.hint')}
                    placeholder={t('fields.email.placeholder')}
                    error={submitted ? errors.email : undefined}
                    required
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    autoComplete="username"
                    autoCapitalize="none"
                    spellCheck={false}
                  />

                  <ForgeButton type="submit" variant="primary" disabled={request.isPending}>
                    {request.isPending
                      ? t('forgotPassword.submitting')
                      : t('forgotPassword.submit')}
                  </ForgeButton>

                  <Link
                    to="/login"
                    className="self-center text-body-sm text-on-forge underline decoration-from-font"
                  >
                    {t('forgotPassword.backToSignIn')}
                  </Link>
                </form>
              </Panel>

              <p className="mt-5 text-center text-micro text-ink-mute">{t('security.note')}</p>
            </>
          )}
        </div>
      </main>
    </div>
  )
}
